import { Archive, Building2, Plus, Search, Shield, UserCheck } from "lucide-react";
import { Button } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import { FilterSelectChip } from "./shared/toolbar/FilterSelectChip";
import { ToggleChip } from "./shared/toolbar/ToggleChip";
import { ClearFilters } from "./shared/toolbar/ClearFilters";

export interface RoleFilters {
  search: string;
  orgId: string | null;
  type: "system" | "custom" | null;
  /** When true, archived (soft-deleted) roles are mixed into the table. */
  showArchived: boolean;
  /** When true, the table is narrowed down to roles the current user belongs to. */
  onlyMine: boolean;
}

interface RolesToolbarProps {
  filters: RoleFilters;
  onFilterChange: <K extends keyof RoleFilters>(key: K, value: RoleFilters[K]) => void;
  orgs: Array<{ id: string; name: string }>;
  onNewRole: () => void;
  canCreate?: boolean;
  /** Count of archived roles hidden behind the "Show archived" toggle (for the badge). */
  archivedCount?: number;
}

export function RolesToolbar({ filters, onFilterChange, orgs, onNewRole, canCreate = true, archivedCount = 0 }: RolesToolbarProps) {
  const { t } = useTranslation();
  const tb = (key: string, opts?: Record<string, unknown>) => t(`iam.dashboard.roles.toolbar.${key}`, opts);

  const TYPE_OPTIONS = [
    { value: "system", label: tb("typeSystem") },
    { value: "custom", label: tb("typeCustom") },
  ] as const;

  const activeFilterCount = [
    filters.orgId, filters.type, filters.showArchived, filters.onlyMine,
  ].filter(Boolean).length;
  const activeOrgName = orgs.find((o) => o.id === filters.orgId)?.name;
  const typeLabel = TYPE_OPTIONS.find((opt) => opt.value === filters.type)?.label;

  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2 py-3">
      {/* Search — fills the row next to the action on mobile; fixed-width on sm+ */}
      <label className="relative order-1 flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-muted-foreground focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40 sm:flex-none">
        <Search className="h-3.5 w-3.5 shrink-0" />
        <input
          type="search"
          placeholder={tb("searchPlaceholder")}
          value={filters.search}
          onChange={(e) => onFilterChange("search", e.target.value)}
          className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70 sm:w-52 sm:flex-none"
        />
        <kbd className="hidden rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:block">
          ⌘K
        </kbd>
      </label>

      {/* Filter chips — own full-width row on mobile; inline on sm+ */}
      <div className="order-3 flex w-full flex-wrap items-center gap-1.5 sm:order-2 sm:w-auto sm:flex-1">
        <FilterSelectChip
          icon={<Building2 className="h-3 w-3 shrink-0" />}
          label={activeOrgName ? tb("filterOrgWithValue", { name: activeOrgName }) : tb("filterOrg")}
          active={!!filters.orgId}
        >
          <select
            value={filters.orgId ?? ""}
            onChange={(e) => onFilterChange("orgId", e.target.value || null)}
            className="absolute inset-0 cursor-pointer opacity-0"
          >
            <option value="">{tb("filterOrgAll")}</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </FilterSelectChip>

        <FilterSelectChip
          icon={<Shield className="h-3 w-3 shrink-0" />}
          label={typeLabel ? tb("filterTypeWithValue", { type: typeLabel }) : tb("filterType")}
          active={!!filters.type}
        >
          <select
            value={filters.type ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              const valid: RoleFilters["type"] = raw === "system" || raw === "custom" ? raw : null;
              onFilterChange("type", valid);
            }}
            className="absolute inset-0 cursor-pointer opacity-0"
          >
            <option value="">{tb("filterTypeAny")}</option>
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </FilterSelectChip>

        {/* Archived toggle — pressed = archived rows mixed into the table */}
        <ToggleChip
          icon={<Archive className="h-3 w-3 shrink-0" />}
          label={tb("showArchived")}
          active={filters.showArchived}
          onClick={() => onFilterChange("showArchived", !filters.showArchived)}
          count={archivedCount}
          title={filters.showArchived ? tb("hideArchivedTooltip") : tb("showArchivedTooltip")}
        />

        {/* "Your role" toggle — pressed = narrowed down to roles the current user belongs to */}
        <ToggleChip
          icon={<UserCheck className="h-3 w-3 shrink-0" />}
          label={tb("onlyMine")}
          active={filters.onlyMine}
          onClick={() => onFilterChange("onlyMine", !filters.onlyMine)}
          title={filters.onlyMine ? tb("onlyMineActiveTooltip") : tb("onlyMineTooltip")}
        />

        {activeFilterCount > 0 && (
          <ClearFilters
            label={tb("clearFilters")}
            onClick={() => {
              onFilterChange("orgId", null);
              onFilterChange("type", null);
              onFilterChange("showArchived", false);
              onFilterChange("onlyMine", false);
            }}
          />
        )}
      </div>

      {/* Primary action — stays on the search row on mobile; at the end on sm+ */}
      {canCreate && (
        <div className="order-2 shrink-0 sm:order-3 sm:ml-auto">
          <Button variant="default" size="sm" className="gap-1.5" onClick={onNewRole}>
            <Plus className="h-3.5 w-3.5" />
            {tb("newRole")}
          </Button>
        </div>
      )}
    </div>
  );
}
